import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
} from 'react';
import type { ReactNode } from 'react';
import type { FacilityLoginResponse } from '../../API/services/loginServices';
import {
  getUserFromStorage,
  setUserToStorage,
  clearUserFromStorage,
} from './storageUtility';

interface AuthContextType {
  user: FacilityLoginResponse | null;
  isAuthenticated: boolean;
  login: (userData: FacilityLoginResponse) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<FacilityLoginResponse | null>(() =>
    getUserFromStorage()
  );

  const login = useCallback((userData: FacilityLoginResponse) => {
    setUserToStorage(userData);
    localStorage.setItem('token', userData.accessToken);
    setUser(userData);
  }, []);

  const logout = useCallback(() => {
    clearUserFromStorage();
    localStorage.removeItem('token');
    sessionStorage.removeItem('pendingMfaUser');
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({
      user,
      isAuthenticated:
        !!user &&
        !!localStorage.getItem('token') &&
        !sessionStorage.getItem('pendingMfaUser'),
      login,
      logout,
    }),
    [user, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
